/**
 * Pilot Activation Checklist · admin-only readiness gate
 * =======================================================
 * Live-evaluated 10+ checks. Closes brutal-audit RED #1
 * (no real customers yet — make sure the platform is plumbing-ready).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import SEOHead from '../../components/SEOHead';
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw,
  Rocket, ExternalLink, BookOpen, Zap,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  ok:      { Icon: CheckCircle2, color: 'emerald', label: 'Green' },
  partial: { Icon: AlertTriangle, color: 'amber',  label: 'Partial' },
  warn:    { Icon: AlertTriangle, color: 'amber',  label: 'Warning' },
  missing: { Icon: XCircle,       color: 'rose',   label: 'Missing' },
};

const PilotActivation = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [quickActivating, setQuickActivating] = useState(false);
  const [quickResult, setQuickResult] = useState(null);
  const [autoPoll, setAutoPoll] = useState(true);

  const fetchChecklist = async (silent = false) => {
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/pilot-activation/checklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load checklist (admin required).');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const quickActivate = async () => {
    if (!window.confirm(
      'This will (1) sign the current-quarter SOC 2 access review on your behalf, and (2) bulk-mark every unreviewed critical/high vendor as reviewed-now. Idempotent — safe to run twice. Proceed?'
    )) return;
    setQuickActivating(true);
    setQuickResult(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/pilot-activation/pilot-quick-activate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuickResult(data);
      await fetchChecklist(); // re-evaluate
    } catch (e) {
      setQuickResult({ ok: false, error: e?.response?.data?.detail || e?.message || 'Action failed' });
    } finally {
      setQuickActivating(false);
    }
  };

  useEffect(() => { fetchChecklist(); }, []);

  // Auto-poll every 30s so the score updates live while the founder is
  // editing backend/.env or adding keys in another tab.
  useEffect(() => {
    if (!autoPoll) return;
    const id = setInterval(() => fetchChecklist(true), 30_000);
    return () => clearInterval(id);
  }, [autoPoll]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEOHead
        title="Pilot Activation · CARTIEAI"
        description="Runtime readiness check before inviting the first paying pilot customer onto the platform."
        noindex
      />

      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-8">
          <Badge className="bg-emerald-500/20 text-emerald-200 mb-3">Admin · Pilot readiness</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="pilot-activation-title">
            <Rocket className="w-7 h-7 inline-block mr-2 text-emerald-400" />
            Pilot activation checklist
          </h1>
          <p className="text-sm sm:text-base text-slate-700 max-w-2xl">
            Every gate that must be green before we invite the first paying pilot.
            Each row is evaluated live against the running system — no hard-coded "yes".
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap text-sm">
            <Link to="/admin/p0-guide" className="inline-flex items-center gap-1.5 text-emerald-300 hover:underline" data-testid="pilot-activation-guide-link">
              <BookOpen className="w-4 h-4" /> Open the P0 guide (how to flip each red row)
            </Link>
            <Link to="/admin/slack-setup" className="inline-flex items-center gap-1.5 text-emerald-300 hover:underline" data-testid="pilot-activation-slack-link">
              <ExternalLink className="w-4 h-4" /> Configure Slack webhook
            </Link>
            <Link to="/admin/cloud-billing-sync" className="inline-flex items-center gap-1.5 text-emerald-300 hover:underline" data-testid="pilot-activation-cbs-link">
              <ExternalLink className="w-4 h-4" /> Azure + GCP billing sync
            </Link>
            <Link to="/admin/incident-log" className="inline-flex items-center gap-1.5 text-emerald-300 hover:underline" data-testid="pilot-activation-incident-log-link">
              <ExternalLink className="w-4 h-4" /> Incident log (CFO narrative)
            </Link>
            <Link to="/admin/zombie-hunter" className="inline-flex items-center gap-1.5 text-emerald-300 hover:underline" data-testid="pilot-activation-zombie-link">
              <ExternalLink className="w-4 h-4" /> Zombie hunter
            </Link>
            <Link to="/admin/payback-filter" className="inline-flex items-center gap-1.5 text-emerald-300 hover:underline" data-testid="pilot-activation-payback-link">
              <ExternalLink className="w-4 h-4" /> Payback filter
            </Link>
            <Button
              onClick={quickActivate}
              disabled={quickActivating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="pilot-quick-activate-btn"
            >
              {quickActivating ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Activating…</>) : (<><Zap className="w-4 h-4 mr-1.5" /> One-click: sign + review all</>)}
            </Button>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 ml-auto" data-testid="pilot-activation-autopoll-label">
              <input
                type="checkbox"
                checked={autoPoll}
                onChange={(e) => setAutoPoll(e.target.checked)}
                data-testid="pilot-activation-autopoll-toggle"
                className="accent-emerald-600"
              />
              Auto-refresh every 30s
            </label>
          </div>
          {quickResult && (
            <div
              className={`mt-3 text-xs p-3 rounded border ${quickResult.ok === false ? 'bg-rose-500/10 border-rose-500/30 text-rose-200' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100'}`}
              data-testid="pilot-quick-activate-result"
            >
              {quickResult.ok === false ? (
                <>Action failed: {quickResult.error}</>
              ) : (
                <>
                  Access review: <b>{quickResult.actions?.access_review?.action}</b> ({quickResult.actions?.access_review?.quarter}).{' '}
                  Vendors reviewed: <b className="tabular-nums">{quickResult.actions?.vendor_risk?.reviewed_count}</b>.{' '}
                  Refresh fired automatically.
                </>
              )}
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" /> Evaluating checklist…
          </div>
        )}

        {err && (
          <Card className="border-rose-500/30 bg-rose-500/10 mb-6">
            <CardContent className="pt-6 text-sm text-rose-200" data-testid="pilot-activation-error">
              {err} <Link to="/login" className="underline ml-2">Log in as admin</Link>
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            <Card className={`mb-6 ${data.score_pct >= 85 ? 'border-emerald-300 bg-emerald-500/10' : data.score_pct >= 65 ? 'border-amber-300 bg-amber-500/10' : 'border-rose-500/40 bg-rose-500/10'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-xs uppercase text-slate-600 tracking-wide">Readiness score</div>
                    <div className="text-4xl font-bold tabular-nums text-slate-900" data-testid="pilot-activation-score">
                      {data.score_pct}%
                    </div>
                    <div className="text-sm text-slate-700 mt-1">{data.ok_count} of {data.total_count} gates green</div>
                  </div>
                  <div className="flex-1 min-w-[200px] max-w-md">
                    <Progress value={data.score_pct} className="h-3" />
                  </div>
                  <Button onClick={fetchChecklist} variant="outline" data-testid="pilot-activation-refresh">
                    <RefreshCw className="w-4 h-4 mr-2" /> Re-evaluate
                  </Button>
                </div>
                <div className="mt-4 text-sm font-medium text-slate-900">{data.verdict}</div>
              </CardContent>
            </Card>

            <Card className="border-emerald-500/20">
              <CardHeader>
                <CardTitle className="text-lg">Live checks</CardTitle>
                <CardDescription className="text-slate-400">{data.evaluated_at}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-200" data-testid="pilot-activation-items">
                  {data.items.map((it) => {
                    const meta = STATUS_META[it.status] || STATUS_META.warn;
                    const Icon = meta.Icon;
                    return (
                      <div key={it.key} className="py-3 flex items-start gap-3" data-testid={`pilot-activation-item-${it.key}`}>
                        <Icon className={`w-5 h-5 mt-0.5 text-${meta.color}-600 shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-900">{it.label}</span>
                            <Badge className={`bg-${meta.color}-100 text-${meta.color}-800`}>{meta.label}</Badge>
                          </div>
                          <div className="text-sm text-slate-600 mt-0.5">{it.detail}</div>
                          {it.action_url && it.action_url.startsWith('/') && (
                            <Link to={it.action_url} className="text-xs text-emerald-300 hover:underline inline-flex items-center gap-1 mt-1">
                              Fix here <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                          {it.action_url && !it.action_url.startsWith('/') && (
                            <code className="text-xs text-slate-600 bg-slate-800 px-1.5 py-0.5 rounded mt-1 inline-block">
                              {it.action_url} (backend/.env)
                            </code>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default PilotActivation;
